/**
 * stageAnimLoader — pure helper for deferred per-stage spritesheet loading.
 *
 * Extracts the set of animation charIds (= assetIds in SpriteAnimRegistry)
 * that a given stage requires. StageScene.preload() calls this so it queues
 * only the sheets needed for the active stage rather than all 40+ sheets at
 * boot.
 *
 * Pure: no Phaser import, no singleton access. Fully unit-testable in node.
 */
import type { StageConfig } from '@/data/stages/stageConfig';

/**
 * Returns the unique set of animation charIds a stage config references.
 *
 * @param cfg        Stage configuration (waves, sections, bossId/finaleGauntlet).
 * @param getAssetId Maps an enemy-archetype id → its spritesheet charId (= assetId).
 *                   Returns undefined for enemies with no animation sheet.
 */
export function stageAnimCharIds(
  cfg: StageConfig,
  getAssetId: (enemyId: string) => string | undefined,
): Set<string> {
  const charIds = new Set<string>();

  const sections = cfg.sections ?? [{ waves: cfg.waves }];
  for (const section of sections) {
    for (const wave of section.waves) {
      for (const group of wave.enemies) {
        const assetId = getAssetId(group.id);
        if (assetId) charIds.add(assetId);
      }
    }
    if (section.eliteId) {
      const assetId = getAssetId(section.eliteId);
      if (assetId) charIds.add(assetId);
    }
  }

  if (cfg.bossId) {
    const assetId = getAssetId(cfg.bossId);
    if (assetId) charIds.add(assetId);
  }

  for (const phase of cfg.finaleGauntlet?.phases ?? []) {
    if (phase.kind === 'boss-rush') {
      for (const bossId of phase.bossIds) {
        const assetId = getAssetId(bossId);
        if (assetId) charIds.add(assetId);
      }
    } else if (phase.kind === 'praetor-handoff' || phase.kind === 'pm-halloway') {
      const assetId = getAssetId(phase.nextBossId);
      if (assetId) charIds.add(assetId);
    }
  }

  return charIds;
}
